// Cloud Explorer store for persisting UI state across tab switches
import { create } from 'zustand';
import { FileType, FileStatus } from '@types';

interface CloudExplorerStore {
  currentFolder: string | null;

  // Filters
  selectedType: FileType | 'all' | 'starred';
  selectedStatus: FileStatus | 'all';
  selectedBpmRange: string;
  selectedKey: string;

  setCurrentFolder: (folderId: string | null) => void;
  setSelectedType: (type: FileType | 'all' | 'starred') => void;
  setSelectedStatus: (status: FileStatus | 'all') => void;
  setSelectedBpmRange: (range: string) => void;
  setSelectedKey: (key: string) => void;
  reset: () => void;
}

const defaultState = {
  currentFolder: null as string | null,
  selectedType: 'all' as FileType | 'all' | 'starred',
  selectedStatus: 'all' as FileStatus | 'all',
  selectedBpmRange: 'all',
  selectedKey: 'all',
};

const loadPersistedState = (): Partial<CloudExplorerStore> => {
  try {
    const stored = sessionStorage.getItem('cloudExplorerState');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        selectedType: parsed.selectedType || defaultState.selectedType,
        selectedStatus: parsed.selectedStatus || defaultState.selectedStatus,
        selectedBpmRange: parsed.selectedBpmRange || defaultState.selectedBpmRange,
        selectedKey: parsed.selectedKey || defaultState.selectedKey,
        currentFolder: null,
      };
    }
  } catch (error) {
    console.warn('Failed to load persisted Cloud Explorer state:', error);
  }
  return {};
};

export const useCloudExplorerStore = create<CloudExplorerStore>((set, get) => {
  const persistedState = loadPersistedState();

  return {
    ...defaultState,
    ...persistedState,

    setCurrentFolder: (folderId) => {
      set({ currentFolder: folderId });
    },
    setSelectedType: (type) => {
      set({ selectedType: type });
      saveState({ ...get(), selectedType: type });
    },
    setSelectedStatus: (status) => {
      set({ selectedStatus: status });
      saveState({ ...get(), selectedStatus: status });
    },
    setSelectedBpmRange: (range) => {
      set({ selectedBpmRange: range });
      saveState({ ...get(), selectedBpmRange: range });
    },
    setSelectedKey: (key) => {
      set({ selectedKey: key });
      saveState({ ...get(), selectedKey: key });
    },

    reset: () => {
      set(defaultState);
      sessionStorage.removeItem('cloudExplorerState');
    },
  };
});

let saveTimeout: NodeJS.Timeout | null = null;
const saveState = (state: CloudExplorerStore) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    try {
      const stateToSave = {
        selectedType: state.selectedType,
        selectedStatus: state.selectedStatus,
        selectedBpmRange: state.selectedBpmRange,
        selectedKey: state.selectedKey,
      };
      sessionStorage.setItem('cloudExplorerState', JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save Cloud Explorer state:', error);
    }
  }, 300);
};
